/**
 * Vincent B784 (Eric 2026-02-18 SPEC STRICK PROD) — Batch detect legacy
 * `aggressive_dedup`.
 *
 * Scanne toutes les vidéos de l'organisation dont le run Whisper a subi
 * un dedup > 50 % (typique des vidéos de coaching) et propose un
 * re-transcription en batch (skip_dedup=true) en 1 clic.
 *
 * Route : `/dashboard/admin/aggressive-dedup` (editor+, via
 * `ProtectedRoute editorOnly`). Utilise le même style que
 * StopwordsAdminPage pour la cohérence visuelle.
 */
import { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useLang } from "../../contexts/LanguageContext";
import { API_BASE as API } from "../../lib/apiBase";

const SINCE_OPTIONS = [
  { value: "30d", label_fr: "30 jours", label_en: "30 days" },
  { value: "90d", label_fr: "90 jours", label_en: "90 days" },
  { value: "180d", label_fr: "180 jours", label_en: "180 days" },
  { value: "365d", label_fr: "1 an", label_en: "1 year" },
  { value: "all", label_fr: "Tout", label_en: "All" },
];

export default function AggressiveDedupAdminPage() {
  const { lang } = useLang();
  const queryClient = useQueryClient();
  const [since, setSince] = useState("90d");
  const [minRatio, setMinRatio] = useState(0.5);
  const [selected, setSelected] = useState(() => new Set());

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["aggressive-dedup", since, minRatio],
    queryFn: async () => {
      const { data } = await axios.get(
        `${API}/api/videos/legacy-aggressive-dedup?since=${since}&min_ratio=${minRatio}`,
        { withCredentials: true }
      );
      return data;
    },
    staleTime: 60_000,
  });

  const candidates = data?.candidates || [];
  const highCount = useMemo(() => candidates.filter((c) => c.level === "high").length, [candidates]);
  const mediumCount = useMemo(() => candidates.filter((c) => c.level === "medium").length, [candidates]);

  const toggleOne = (vid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(vid)) next.delete(vid);
      else next.add(vid);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map((c) => c.video_id)));
  };
  const selectHigh = () => setSelected(new Set(candidates.filter((c) => c.level === "high").map((c) => c.video_id)));

  const rerunMutation = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post(
        `${API}/api/videos/legacy-aggressive-dedup/rerun-batch`,
        { video_ids: Array.from(selected) },
        { withCredentials: true, timeout: 90000 }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(
        lang === "fr"
          ? `${data.queued} vidéo(s) mise(s) en file, ${data.skipped.length} ignorée(s)`
          : `${data.queued} video(s) queued, ${data.skipped.length} skipped`,
        { duration: 8000 }
      );
      // Vincent B786 (Eric 2026-02-18 SPEC STRICK PROD) — Toast persistant
      // qui poll `/pipeline-status-batch` toutes les 5 s pour montrer
      // la progression du batch en temps réel.
      const queuedIds = Array.from(selected).filter(
        (vid) => !(data.skipped || []).some((s) => s.video_id === vid),
      );
      if (queuedIds.length > 0) {
        startBatchProgressPoller(queuedIds);
      }
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ["aggressive-dedup"] });
    },
    onError: (err) => {
      toast.error(
        lang === "fr"
          ? `Erreur : ${err.response?.data?.detail || err.message}`
          : `Error: ${err.response?.data?.detail || err.message}`
      );
    },
  });

  // Vincent B786 (Eric 2026-02-18 SPEC STRICK PROD) — Poller de progression.
  // Un seul intervalle actif à la fois (ref + cleanup au démontage).
  const pollerRef = useRef(null);
  const pollingToastIdRef = useRef(null);
  const [batchProgress, setBatchProgress] = useState(null); // {total, done, running, failed}

  useEffect(() => () => {
    // Cleanup au démontage : arrête le poller + retire le toast.
    if (pollerRef.current) clearInterval(pollerRef.current);
    if (pollingToastIdRef.current) toast.dismiss(pollingToastIdRef.current);
  }, []);

  const startBatchProgressPoller = (ids) => {
    // Arrête un poller en cours (au cas où Vincent relance sans attendre).
    if (pollerRef.current) clearInterval(pollerRef.current);
    if (pollingToastIdRef.current) toast.dismiss(pollingToastIdRef.current);
    const total = ids.length;
    const toastId = toast.loading(
      lang === "fr"
        ? `⚙️ Re-transcription batch : 0 / ${total} terminées…`
        : `⚙️ Batch re-transcribing: 0 / ${total} done…`,
      { duration: Infinity }
    );
    pollingToastIdRef.current = toastId;
    setBatchProgress({ total, done: 0, running: total, failed: 0 });

    const tick = async () => {
      try {
        const { data } = await axios.post(
          `${API}/api/videos/pipeline-status-batch`,
          { video_ids: ids },
          { withCredentials: true, timeout: 15000 }
        );
        const results = data?.results || {};
        let done = 0;
        let failed = 0;
        let running = 0;
        for (const vid of ids) {
          const r = results[vid];
          const status = r?.pipeline_status;
          if (status === "complete" || status === "paused_pending_fusion") done += 1;
          else if (status === "failed") failed += 1;
          else running += 1;
        }
        setBatchProgress({ total, done, running, failed });
        const remaining = total - done - failed;
        if (remaining <= 0) {
          // Terminé : toast final + arrêt poller.
          clearInterval(pollerRef.current);
          pollerRef.current = null;
          if (failed > 0) {
            toast.error(
              lang === "fr"
                ? `✅ ${done} terminée(s), ❌ ${failed} en échec sur ${total}`
                : `✅ ${done} done, ❌ ${failed} failed of ${total}`,
              { id: toastId, duration: 15000 }
            );
          } else {
            toast.success(
              lang === "fr"
                ? `✅ Batch terminé — ${done} / ${total} vidéos re-transcrites`
                : `✅ Batch complete — ${done} / ${total} videos re-transcribed`,
              { id: toastId, duration: 10000 }
            );
          }
          pollingToastIdRef.current = null;
          queryClient.invalidateQueries({ queryKey: ["aggressive-dedup"] });
        } else {
          toast.loading(
            lang === "fr"
              ? `⚙️ Re-transcription batch : ${done} / ${total} terminées${failed > 0 ? ` (${failed} échec${failed > 1 ? "s" : ""})` : ""}…`
              : `⚙️ Batch re-transcribing: ${done} / ${total} done${failed > 0 ? ` (${failed} failed)` : ""}…`,
            { id: toastId, duration: Infinity }
          );
        }
      } catch (_e) {
        // Erreur transitoire — on continue à poller, mais on met à jour le toast.
        toast.loading(
          lang === "fr"
            ? `⚠️ Batch en cours — vérification suspendue temporairement…`
            : `⚠️ Batch running — status check temporarily paused…`,
          { id: toastId, duration: Infinity }
        );
      }
    };
    // Fire immédiat + toutes les 5 s.
    tick();
    pollerRef.current = setInterval(tick, 5000);
  };

  const handleRerun = () => {
    if (selected.size === 0) return;
    const msg = lang === "fr"
      ? `Relancer Whisper SANS dedup sur ${selected.size} vidéo(s) ?\n\nChaque vidéo va :\n  • Effacer son transcript Whisper actuel\n  • Se re-transcrire en gardant TOUS les segments\n  • Prendre 30 s à 5 min selon sa durée\n\nLa file (semaphore=1) les traite une à la fois.`
      : `Re-run Whisper WITHOUT dedup on ${selected.size} video(s)?`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    rerunMutation.mutate();
  };

  const fmtDuration = (s) => {
    if (!s) return "?";
    const m = Math.floor(s / 60);
    const r = Math.round(s % 60);
    return `${m}m ${String(r).padStart(2, "0")}s`;
  };
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA") : "?";

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="aggressive-dedup-admin-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-zinc-900 mb-1">
          {lang === "fr" ? "Diagnostic — Filtre anti-hallucination agressif" : "Diagnostic — Aggressive anti-hallucination filter"}
        </h1>
        <p className="text-sm text-zinc-600 leading-snug">
          {lang === "fr"
            ? "Vidéos dont le filtre a retiré ≥ 50 % des segments Whisper (typique des audios de coaching, voix douces, enchaînements courts). Sélectionne-les puis relance en batch avec le filtre désactivé pour récupérer les transcriptions complètes."
            : "Videos where the filter removed ≥ 50 % of Whisper segments (typical of coaching, quiet voices, quick exchanges). Select and batch re-run with the filter off to recover full transcripts."}
        </p>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-zinc-50 border border-zinc-200 rounded">
        <label className="text-xs text-zinc-700 flex items-center gap-2">
          <span>{lang === "fr" ? "Depuis" : "Since"}</span>
          <select
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="border border-zinc-300 rounded px-2 py-1 text-xs"
            data-testid="aggressive-dedup-since-select"
          >
            {SINCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{lang === "fr" ? o.label_fr : o.label_en}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-zinc-700 flex items-center gap-2">
          <span>{lang === "fr" ? "Seuil ratio" : "Min ratio"}</span>
          <input
            type="number"
            min="0.1"
            max="0.99"
            step="0.05"
            value={minRatio}
            onChange={(e) => setMinRatio(Number(e.target.value) || 0.5)}
            className="border border-zinc-300 rounded px-2 py-1 text-xs w-16 tabular-nums"
            data-testid="aggressive-dedup-min-ratio-input"
          />
          <span className="text-zinc-500 tabular-nums">({Math.round(minRatio * 100)} %)</span>
        </label>
        <div className="ml-auto flex items-center gap-3 text-[11px] text-zinc-500 tabular-nums">
          <span>
            {lang === "fr" ? "Trouvées :" : "Found:"}{" "}
            <span className="font-bold text-zinc-900" data-testid="aggressive-dedup-count-total">{data?.count ?? "…"}</span>
          </span>
          {highCount > 0 && (
            <span className="text-amber-800" data-testid="aggressive-dedup-count-high">
              🔴 {highCount} high
            </span>
          )}
          {mediumCount > 0 && (
            <span className="text-amber-600" data-testid="aggressive-dedup-count-medium">
              🟡 {mediumCount} medium
            </span>
          )}
        </div>
      </div>

      {/* Actions batch */}
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={toggleAll}
          disabled={isPending || candidates.length === 0}
          className="text-xs px-3 py-1.5 border border-zinc-300 rounded hover:bg-zinc-50 disabled:opacity-50"
          data-testid="aggressive-dedup-toggle-all-btn"
        >
          {selected.size === candidates.length && candidates.length > 0
            ? (lang === "fr" ? "Tout désélectionner" : "Deselect all")
            : (lang === "fr" ? "Tout sélectionner" : "Select all")}
        </button>
        <button
          onClick={selectHigh}
          disabled={isPending || highCount === 0}
          className="text-xs px-3 py-1.5 border border-amber-400 text-amber-800 rounded hover:bg-amber-50 disabled:opacity-50"
          data-testid="aggressive-dedup-select-high-btn"
        >
          🔴 {lang === "fr" ? `Sélectionner high (${highCount})` : `Select high (${highCount})`}
        </button>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-zinc-500 tabular-nums" data-testid="aggressive-dedup-selected-count">
            {selected.size} {lang === "fr" ? "sélectionnée(s)" : "selected"}
          </span>
          <button
            onClick={handleRerun}
            disabled={selected.size === 0 || rerunMutation.isPending}
            className="inline-flex items-center gap-1.5 text-xs px-4 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white font-semibold shadow-sm disabled:opacity-50"
            data-testid="aggressive-dedup-rerun-batch-btn"
          >
            <span aria-hidden="true">⚠️</span>
            {rerunMutation.isPending
              ? (lang === "fr" ? "Relance en cours…" : "Re-running…")
              : (lang === "fr" ? `Re-transcrire ${selected.size} sans dedup` : `Re-transcribe ${selected.size} without dedup`)}
          </button>
        </div>
      </div>

      {/* Tableau */}
      {isPending ? (
        <p className="text-sm text-zinc-500 italic">{lang === "fr" ? "Chargement…" : "Loading…"}</p>
      ) : isError ? (
        <p className="text-sm text-rose-600">
          {lang === "fr" ? "Erreur au chargement — essaie de recharger." : "Load error — try reloading."}
          <button onClick={() => refetch()} className="ml-2 underline">{lang === "fr" ? "Réessayer" : "Retry"}</button>
        </p>
      ) : candidates.length === 0 ? (
        <div className="text-center py-12 text-sm text-zinc-500 border border-dashed border-zinc-200 rounded" data-testid="aggressive-dedup-empty">
          <p className="text-4xl mb-2">✅</p>
          <p className="font-semibold text-emerald-700 mb-1">
            {lang === "fr" ? "Aucune vidéo affectée sur cette période." : "No affected videos in this window."}
          </p>
          <p>
            {lang === "fr" ? "Le filtre anti-hallucination fonctionne bien pour ton contenu récent." : "The anti-hallucination filter is working well for your recent content."}
          </p>
        </div>
      ) : (
        <div className="border border-zinc-200 rounded overflow-hidden" data-testid="aggressive-dedup-table">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-600">
              <tr>
                <th className="px-2 py-2 w-8 text-center">
                  <input
                    type="checkbox"
                    checked={selected.size === candidates.length && candidates.length > 0}
                    onChange={toggleAll}
                    data-testid="aggressive-dedup-header-checkbox"
                  />
                </th>
                <th className="px-2 py-2 text-left">{lang === "fr" ? "Vidéo" : "Video"}</th>
                <th className="px-2 py-2 text-right">{lang === "fr" ? "Durée" : "Duration"}</th>
                <th className="px-2 py-2 text-right">{lang === "fr" ? "Date" : "Date"}</th>
                <th className="px-2 py-2 text-right">{lang === "fr" ? "Retirés" : "Removed"}</th>
                <th className="px-2 py-2 text-right">Ratio</th>
                <th className="px-2 py-2 text-center">Level</th>
                <th className="px-2 py-2 text-left">{lang === "fr" ? "Statut" : "Status"}</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => (
                <tr
                  key={c.video_id}
                  className={`border-t border-zinc-100 hover:bg-zinc-50 ${selected.has(c.video_id) ? "bg-amber-50/60" : ""}`}
                  data-testid={`aggressive-dedup-row-${c.video_id}`}
                >
                  <td className="px-2 py-1.5 text-center">
                    <input
                      type="checkbox"
                      checked={selected.has(c.video_id)}
                      onChange={() => toggleOne(c.video_id)}
                      data-testid={`aggressive-dedup-checkbox-${c.video_id}`}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <a
                      href={`/dashboard/videos/${c.video_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#002FA7] hover:underline"
                      title={c.video_id}
                    >
                      {c.title}
                    </a>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-zinc-600">{fmtDuration(c.duration_seconds)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-zinc-500">{fmtDate(c.created_at)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    <span className="text-zinc-700 font-semibold">{c.dedup_stats?.removed || 0}</span>
                    <span className="text-zinc-400"> / {c.dedup_stats?.before_dedup || 0}</span>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-bold">
                    {Math.round(c.ratio * 100)} %
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    {c.level === "high" ? (
                      <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-200 text-amber-900">HIGH</span>
                    ) : (
                      <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">MEDIUM</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-[10px] text-zinc-500">{c.pipeline_status || "?"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-zinc-500 italic mt-3">
        {lang === "fr"
          ? "ℹ️ Les vidéos déjà en `processing` / `pending` sont ignorées automatiquement (évite les doublons)."
          : "ℹ️ Videos already in `processing` / `pending` are skipped (avoids duplicates)."}
      </p>
    </div>
  );
}
